/**
 * One moment in a session's history, as the backend records it: made,
 * messaged, moved, settled or paid. Only kinds, statuses, channels and times —
 * never a title, a note or a contact detail.
 */

export type SessionEventActor = "therapist" | "patient";
export type SessionProgressStatus = "pending" | "finished" | "cancelled";
export type SessionPaymentStatus = "pending" | "paid";
/** Where a payment change came from: the session, its payment record, or an online payment. */
export type PaymentChangeSource = "session_edit" | "payment_record" | "online_payment";
export type NotificationKind = "confirmation" | "reschedule" | "cancellation" | "reminder";
export type NotificationChannel = "email" | "whatsapp";
export type NotificationRecipient = "patient" | "therapist";
/** `opened`: the therapist opened the chat on their own WhatsApp; sending is up to them. */
export type NotificationOutcome = "sent" | "failed" | "opened";

type EventBase = {
    id: string;
    occurredAt: Date;
    /** Null when the system did it: a sweep, a background worker. */
    actor: SessionEventActor | null;
};

export type SessionCreatedEvent = EventBase & {
    type: "created";
    /** `import`: a session held before Alchely, brought in afterwards. */
    origin: "booking" | "direct" | "import";
    initialStatus: SessionProgressStatus;
    bookedBy: SessionEventActor | null;
};

export type SessionStatusChangedEvent = EventBase & {
    type: "status_changed";
    from: SessionProgressStatus;
    to: SessionProgressStatus;
    /** Who asked for the cancellation `to` is — not whose hand recorded it. */
    cancelledBy: SessionEventActor | null;
    /** Who had asked for the cancellation `from` is. */
    previouslyCancelledBy: SessionEventActor | null;
};

export type SessionPaymentStatusChangedEvent = EventBase & {
    type: "payment_status_changed";
    from: SessionPaymentStatus;
    to: SessionPaymentStatus;
    source: PaymentChangeSource;
};

export type SessionRescheduledEvent = EventBase & {
    type: "rescheduled";
    /** The therapist's wall clock, "YYYY-MM-DDTHH:MM", as the diary shows it. */
    fromStart: string;
    toStart: string;
};

export type SessionNotificationEvent = EventBase & {
    type: "notification";
    kind: NotificationKind;
    channel: NotificationChannel;
    recipient: NotificationRecipient;
    outcome: NotificationOutcome;
    recipientCount: number;
};

export type SessionEvent =
    | SessionCreatedEvent
    | SessionStatusChangedEvent
    | SessionPaymentStatusChangedEvent
    | SessionRescheduledEvent
    | SessionNotificationEvent;

/** A wall-clock start as a Date in the viewer's zone, for formatting only. */
export function wallClockToDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;
    const [, year, month, day, hour, minute] = match.map(Number);
    return new Date(year, month - 1, day, hour, minute);
}
